import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL } from '@/config/siteConfig';
import { buildCanonicalUrl } from '@/lib/seo/canonicalUrl';
import { localizedRoutes, getPrerenderPath, getLocalizedPath, getRouteKeyFromPath, stripHtmlSuffix, RouteKey } from '@/lib/i18n/routes';
import type { Language } from '@/lib/i18n/translations-meta';

// ---------------------------------------------------------------------------
// Multilingual helpers
// ---------------------------------------------------------------------------

const SUPPORTED_LANGUAGES: Language[] = ['en', 'es', 'de', 'fr', 'nl', 'it', 'pt', 'dk'];

// Convert internal language code to BCP47 (dk → da) for schema.org inLanguage
function toBcp47(lang: Language): string {
  return lang === 'dk' ? 'da' : lang;
}

function localizedHomeUrl(lang: Language): string {
  return `${SITE_URL}${getPrerenderPath(localizedRoutes[lang].home)}`;
}

function localizedRouteUrl(routeKey: RouteKey, lang: Language): string {
  return `${SITE_URL}${getPrerenderPath(localizedRoutes[lang][routeKey])}`;
}

/**
 * Language-specific entity identifiers.
 *
 * Each language variant is its own node, anchored on that language's home
 * canonical (e.g. https://www.i-rent.net/de/#organization), so the `@id` and
 * `url` of the site-wide Organization/WebSite nodes always match the language
 * of the page they are embedded in — and never collide across variants.
 */
function organizationId(lang: Language): string {
  return `${localizedHomeUrl(lang)}#organization`;
}

function webSiteId(lang: Language): string {
  return `${localizedHomeUrl(lang)}#website`;
}


// Localized organization descriptions (short, ≤160 chars per locale)
const ORG_DESCRIPTIONS: Record<Language, string> = {
  en: 'All-in-one holiday rental management software with PMS, Channel Manager and Website Builder for agencies and property managers.',
  es: 'Software todo-en-uno para gestión de alquileres vacacionales con PMS, Channel Manager y creador de sitios web para agencias.',
  de: 'All-in-one-Software für Ferienvermietung mit PMS, Channel Manager und Website-Builder für Agenturen und Verwalter.',
  fr: 'Logiciel tout-en-un de gestion de locations de vacances avec PMS, Channel Manager et créateur de site web pour agences.',
  nl: 'All-in-one software voor vakantieverhuur met PMS, Channel Manager en Website Builder voor agentschappen en beheerders.',
  it: 'Software all-in-one per la gestione degli affitti vacanze con PMS, Channel Manager e Website Builder per agenzie.',
  pt: 'Software tudo-em-um para gestão de alugueres de férias com PMS, Channel Manager e Website Builder para agências.',
  dk: 'Alt-i-en software til ferieudlejning med PMS, Channel Manager og Website Builder til bureauer og administratorer.',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CommonSchemaProps {
  language?: Language;
}

interface OrganizationSchemaProps extends CommonSchemaProps {
  type: 'organization';
}

interface FAQSchemaProps extends CommonSchemaProps {
  type: 'faq';
  faqs: Array<{ question: string; answer: string }>;
}

interface ArticleSchemaProps extends CommonSchemaProps {
  type: 'article';
  headline: string;
  description: string;
  datePublished: string;
  author: string;
  image?: string;
  /** Optional override; defaults to the page canonical. */
  url?: string;
}

interface BreadcrumbItem {
  name: string;
  /**
   * Absolute or relative URL. Prefer passing `routeKey` instead: it is
   * resolved against the localized route table at schema-generation time, so
   * the URL can never be stale or language-mismatched. Empty means
   * "current page" (the last crumb).
   */
  url?: string;
  /** Preferred: resolved to the localized route base for the active language. */
  routeKey?: RouteKey;
}


interface BreadcrumbSchemaProps extends CommonSchemaProps {
  type: 'breadcrumb';
  items: BreadcrumbItem[];
}

interface SoftwareApplicationSchemaProps extends CommonSchemaProps {
  type: 'software';
  name: string;
  description: string;
  applicationCategory: string;
  url?: string;
}

interface WebSiteSchemaProps extends CommonSchemaProps {
  type: 'website';
}

interface ProductSchemaProps extends CommonSchemaProps {
  type: 'product';
  name: string;
  description: string;
  image?: string;
  url?: string;
  brand?: string;
  category?: string;
  offers?: Array<{
    name?: string;
    price: string;
    priceCurrency?: string;
    url?: string;
    description?: string;
  }>;
  aggregateRating?: {
    ratingValue: string | number;
    reviewCount: string | number;
  };
}

type JsonLdSchemaProps =
  | OrganizationSchemaProps
  | FAQSchemaProps
  | ArticleSchemaProps
  | BreadcrumbSchemaProps
  | SoftwareApplicationSchemaProps
  | WebSiteSchemaProps
  | ProductSchemaProps;

// ---------------------------------------------------------------------------
// Schema builders
// ---------------------------------------------------------------------------

function generateOrganizationSchema(lang: Language) {
  const homeUrl = localizedHomeUrl(lang);
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId(lang),
    name: 'i-Rent.net',
    url: homeUrl,
    mainEntityOfPage: { '@id': webSiteId(lang) },
    inLanguage: toBcp47(lang),

    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/__l5e/assets-v1/7a7c39b1-e44a-43f6-9ee7-434b34ebe79d/logo.webp`,
    },
    description: ORG_DESCRIPTIONS[lang] ?? ORG_DESCRIPTIONS.en,
    foundingDate: '2010',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English', 'Spanish', 'German', 'French', 'Dutch', 'Italian', 'Portuguese', 'Danish'],
    },
    sameAs: [
      'https://www.facebook.com/irentnet',
      'https://twitter.com/irentnet',
      'https://www.linkedin.com/company/i-rent-net',
      // Cross-language self-references help search engines connect variants
      ...SUPPORTED_LANGUAGES.filter((l) => l !== lang).map(localizedHomeUrl),
    ],
  };
}

function generateFAQSchema(faqs: Array<{ question: string; answer: string }>, lang: Language, pageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    url: pageUrl,
    inLanguage: toBcp47(lang),
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

function generateArticleSchema(props: ArticleSchemaProps, lang: Language, pageUrl: string) {
  const url = props.url || pageUrl;
  // Google requires/recommends an image on Article; fall back to the shared
  // editorial OG image when a post ships without its own artwork.
  const rawImage = props.image?.trim();
  const image = rawImage
    ? (/^https?:\/\//i.test(rawImage) ? rawImage : `${SITE_URL}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`)
    : `${SITE_URL}/images/og-blog.jpg`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${url}#article`,
    url,
    inLanguage: toBcp47(lang),
    headline: props.headline,
    description: props.description,
    datePublished: props.datePublished,
    dateModified: props.datePublished,
    author: {
      '@type': 'Person',
      name: props.author,
    },
    publisher: {
      '@type': 'Organization',
      '@id': organizationId(lang),
      name: 'i-Rent.net',
      url: localizedHomeUrl(lang),
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/__l5e/assets-v1/7a7c39b1-e44a-43f6-9ee7-434b34ebe79d/logo.webp`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
    },
    isPartOf: { '@id': webSiteId(lang) },
    image,
  };
}

/**
 * Resolve a breadcrumb item to an absolute, language-correct URL.
 *
 * Hardening rules (in priority order):
 *  1. `routeKey` wins — always resolved against the localized route table for
 *     the active language, so async/lazy route or content loading can never
 *     leak an English (or stale) path into a localized page's breadcrumbs.
 *  2. A supplied `url` is normalized: made absolute, forced onto the canonical
 *     origin, `.html` stripped, and — when it maps to a known route — rewritten
 *     to that route's localized path for the active language.
 *  3. A path that is not a known route gets the active language prefix.
 *  4. Anything unusable resolves to `undefined` and the crumb is emitted
 *     without an `item` (valid for the final crumb).
 */
function resolveBreadcrumbItemUrl(
  item: BreadcrumbItem,
  lang: Language,
): string | undefined {
  if (item.routeKey && (localizedRoutes[lang]?.[item.routeKey] || localizedRoutes.en?.[item.routeKey])) {
    return `${SITE_URL}${getLocalizedPath(item.routeKey, lang)}`;
  }

  const raw = item.url?.trim();
  if (!raw) return undefined;

  let path: string;
  try {
    // Relative values resolve against the canonical origin; absolute values on
    // a foreign origin are re-homed onto it (breadcrumbs are always internal).
    path = new URL(raw, SITE_URL).pathname;
  } catch {
    return undefined;
  }

  const normalized = getPrerenderPath(stripHtmlSuffix(path));

  // Known route? Re-emit it in the active language, whatever language the
  // caller happened to build the URL in.
  const routeKey = getRouteKeyFromPath(normalized);
  if (routeKey) {
    return `${SITE_URL}${getLocalizedPath(routeKey, lang)}`;
  }

  // Unknown path (e.g. a blog post slug): strip any foreign language prefix
  // and re-apply the active language's prefix.
  const langPrefixRe = new RegExp(`^/(${SUPPORTED_LANGUAGES.join('|')})(?=/|$)`);
  const bare = normalized.replace(langPrefixRe, '') || '/';
  const prefix = lang === 'en' ? '' : `/${lang}`;

  return `${SITE_URL}${prefix}${bare === '/' && prefix ? '' : bare}`;
}

function generateBreadcrumbSchema(items: BreadcrumbItem[], lang: Language, pageUrl: string) {
  const resolved = items
    .filter((item) => item.name)
    .map((item, index) => {
      // The last crumb is the current page: default it to the page canonical
      // so the trail is always fully resolvable.
      const isLast = index === items.length - 1;
      const url = resolveBreadcrumbItemUrl(item, lang) ?? (isLast ? pageUrl : undefined);
      return { name: item.name, url };
    });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    inLanguage: toBcp47(lang),
    itemListElement: resolved.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url && { item: item.url }),
    })),
  };
}


function generateSoftwareApplicationSchema(
  props: SoftwareApplicationSchemaProps,
  lang: Language,
  pageUrl: string,
) {
  const url = props.url || pageUrl;
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${url}#software`,
    name: props.name,
    description: props.description,
    applicationCategory: props.applicationCategory,
    operatingSystem: 'Web Browser',
    url,
    inLanguage: toBcp47(lang),
    availableLanguage: SUPPORTED_LANGUAGES.map(toBcp47),
    publisher: { '@id': organizationId(lang) },
    isPartOf: { '@id': webSiteId(lang) },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Free trial available',
      availability: 'https://schema.org/InStock',
      // Offers must point at a page where the offer can be acted on: the
      // localized pricing page.
      url: localizedRouteUrl('pricing', lang),
    },
  };
}

function generateWebSiteSchema(lang: Language) {
  const homeUrl = localizedHomeUrl(lang);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': webSiteId(lang),
    name: 'i-Rent.net',
    url: homeUrl,
    // Language variant of this WebSite node — the site as a whole is exposed
    // through the per-language workTranslation references below.
    inLanguage: toBcp47(lang),
    publisher: { '@id': organizationId(lang) },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${localizedRouteUrl('blog', lang)}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    // Cross-language alternates so crawlers understand the localized variants
    workTranslation: SUPPORTED_LANGUAGES.filter((l) => l !== lang).map((l) => ({
      '@type': 'WebSite',
      '@id': webSiteId(l),
      url: localizedHomeUrl(l),
      inLanguage: toBcp47(l),
    })),
  };
}


function generateProductSchema(props: ProductSchemaProps, lang: Language, pageUrl: string) {
  const url = props.url || pageUrl;
  const offers = (props.offers && props.offers.length > 0)
    ? props.offers.map((o) => ({
        '@type': 'Offer',
        ...(o.name && { name: o.name }),
        price: o.price,
        priceCurrency: o.priceCurrency || 'EUR',
        url: o.url || localizedRouteUrl('pricing', lang),
        ...(o.description && { description: o.description }),
        availability: 'https://schema.org/InStock',
      }))
    : [{
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        description: 'Free trial available',
        availability: 'https://schema.org/InStock',
        url: localizedRouteUrl('pricing', lang),
      }];

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${url}#product`,
    url,
    name: props.name,
    description: props.description,
    inLanguage: toBcp47(lang),
    ...(props.image && { image: props.image }),
    ...(props.category && { category: props.category }),
    brand: {
      '@type': 'Brand',
      name: props.brand || 'i-Rent.net',
    },
    manufacturer: { '@id': organizationId(lang) },
    isPartOf: { '@id': webSiteId(lang) },
    offers: offers.length === 1 ? offers[0] : offers,

    ...(props.aggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: props.aggregateRating.ratingValue,
        reviewCount: props.aggregateRating.reviewCount,
      },
    }),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JsonLdSchema(props: JsonLdSchemaProps) {
  const location = useLocation();
  // Language-specific canonical of the page this schema is embedded in — all
  // entity `url`/`@id` values derive from it so JSON-LD never disagrees with
  // <link rel="canonical">.
  const pageUrl = buildCanonicalUrl(location.pathname);

  useEffect(() => {
    const lang: Language = props.language ?? 'en';
    let schema: object;

    switch (props.type) {
      case 'organization':
        schema = generateOrganizationSchema(lang);
        break;
      case 'faq':
        schema = generateFAQSchema(props.faqs, lang, pageUrl);
        break;
      case 'article':
        schema = generateArticleSchema(props, lang, pageUrl);
        break;
      case 'breadcrumb':
        schema = generateBreadcrumbSchema(props.items, lang, pageUrl);
        break;
      case 'software':
        schema = generateSoftwareApplicationSchema(props, lang, pageUrl);
        break;
      case 'website':
        schema = generateWebSiteSchema(lang);
        break;
      case 'product':
        schema = generateProductSchema(props, lang, pageUrl);
        break;
      default:
        return;
    }

    // Stable per-type id so language switches update rather than duplicate.
    const scriptId = `json-ld-${props.type}`;

    const inject = () => {
      // Drop the build-time prerendered FAQ node so hydration never leaves two
      // FAQPage blocks in the head.
      if (props.type === 'faq') {
        document.head
          .querySelectorAll('script[data-prerendered-faq="true"]')
          .forEach((el) => el.remove());
      }
      let existingScript = document.getElementById(scriptId);
      if (!existingScript) {
        existingScript = document.createElement('script');
        existingScript.id = scriptId;
        existingScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(existingScript);
      }
      existingScript.textContent = JSON.stringify(schema);
    };

    // Structured data must never reach the head before <link rel="canonical">:
    // validators resolve every node's expected @id/url from the canonical, and
    // on async pages (blog posts) the canonical lands a tick later. Wait for it
    // (with a safety timeout) instead of racing it.
    let observer: MutationObserver | undefined;
    let timeoutId: number | undefined;
    const done = () => {
      observer?.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    if (document.querySelector('link[rel="canonical"]')) {
      inject();
    } else {
      observer = new MutationObserver(() => {
        if (document.querySelector('link[rel="canonical"]')) {
          done();
          inject();
        }
      });
      observer.observe(document.head, { childList: true });
      // Fallback: pages that legitimately have no canonical (soft-404) must
      // still get their schema rather than hanging forever.
      timeoutId = window.setTimeout(() => {
        done();
        inject();
      }, 3000);
    }

    return () => {
      done();
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, [props, pageUrl]);


  return null;
}
